import useAssistantPanelSendPrompt from "components/AssistantPanel/hooks/useSendPrompt";
import useAssistantPanelAnalytics from "components/AssistantPanel/analytics/useAnalytics";
import useRunSummary from "components/RunSummarization/hooks/useRunSummary";
import { buildRunSummaryPrompt } from "components/RunSummarization/prompts";
import RunSummarizationTrigger from "components/RunSummarization/RunSummarizationTrigger";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AccessResult } from "utils/access";

import PerformTaskAgainButton from "../PerformTaskAgainButton";
import RetryRunButton from "../RetryRunButton";

type UnfinishedRunActionsProps = {
  stackId: string;
  runId: string;
  canRetry: AccessResult;
  runRetryBlocker: string | null;
  isTaskRun: boolean;
  isModuleRun: boolean;
  canPerformAgain?: AccessResult;
  runTaskCommand: string | null;
};

const UnfinishedRunActions = ({
  stackId,
  runId,
  canRetry,
  runRetryBlocker,
  isTaskRun,
  isModuleRun,
  canPerformAgain,
  runTaskCommand,
}: UnfinishedRunActionsProps) => {
  const { openPrompt, isChatOpen } = useAssistantPanelSendPrompt();
  const trackSegmentAnalyticsEvent = useAnalytics();
  const { trackConversationOpened } = useAssistantPanelAnalytics();

  const { spaceliftIntelligenceTermsAccepted, canTriggerSummary, showSummarizerTrigger } =
    useRunSummary();

  const disableAiTrigger = !canTriggerSummary.result || !spaceliftIntelligenceTermsAccepted;

  const generateTooltipMessage = () => {
    if (!spaceliftIntelligenceTermsAccepted) {
      return "An account admin must accept the terms and conditions before using Infra Assistant";
    }

    if (!canTriggerSummary.result && canTriggerSummary.message) {
      return canTriggerSummary.message;
    }

    return (
      <Box direction="column" gap="small">
        <Typography tag="p">Ask Infra Assistant</Typography>
        <Typography tag="p">
          Infra Assistant combines information from <b>all execution phases</b> to give a
          comprehensive run summary, which may take longer to process than the phase summaries.
        </Typography>
      </Box>
    );
  };

  const handleAssistantTrigger = () => {
    trackSegmentAnalyticsEvent("Run Logs - AI Run Suggestions Clicked", {
      stackId,
      runId,
      via: "assistant",
    });

    trackConversationOpened("run_summarization");

    const prompt = buildRunSummaryPrompt({ stackId, runId });

    openPrompt(prompt, { newChat: !isChatOpen });
  };

  return (
    <>
      <Box gap="medium">
        {(canPerformAgain?.result || canPerformAgain?.message) && (
          <PerformTaskAgainButton
            command={runTaskCommand}
            stackId={stackId}
            errorMessage={canPerformAgain.message}
          />
        )}
        {(canRetry.result || canRetry.message || (runRetryBlocker && !isTaskRun)) && (
          <RetryRunButton
            errorMessage={canRetry.message}
            runId={runId}
            stackId={stackId}
            runRetryBlocker={runRetryBlocker}
            isModuleRun={isModuleRun}
          />
        )}
        {showSummarizerTrigger && (
          <RunSummarizationTrigger
            onPress={handleAssistantTrigger}
            disabled={disableAiTrigger}
            tooltipText={generateTooltipMessage()}
          >
            Explain
          </RunSummarizationTrigger>
        )}
      </Box>
    </>
  );
};

export default UnfinishedRunActions;
